/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { CheckOutlined } from '@ant-design/icons';
import { Grid } from 'antd';

import { MimicLogo } from '@/components/MimicLogo';
import { Button } from '@/primitives/Button';
import { Space } from '@/primitives/Space';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

const { useBreakpoint } = Grid;

export type GrantOAuthProps = {
  clientName: string;
  scopes: string[];
  authorizeUrl: string;
  translations: {
    title: string;
    description: string;
    permissionsTitle: string;
    grantButton: string;
    disclaimer: string;
  };
};

export function GrantOAuth({ clientName, scopes, authorizeUrl, translations }: GrantOAuthProps) {
  const screens = useBreakpoint();

  const containerStyle = {
    padding: screens.sm ? '42px' : '34px',
    border: screens.sm ? '1px solid #EFEFEE' : 'none',
    width: 600,
  };

  return (
    <div style={containerStyle}>
      <Space dir="vertical" size="md" width="100%">
        <MimicLogo width="8rem" height="3rem" color="#222121" />
        <Space dir="vertical" size="sm">
          <Title style={{ margin: 0 }}>{translations.title}</Title>
          <Text size="xl" lineHeight="1.75rem">
            <strong>{clientName}</strong> {translations.description}
          </Text>
        </Space>

        <Space dir="vertical" size="md" width="100%">
          <Text size="md">{translations.permissionsTitle}</Text>
          <div
            style={{
              backgroundColor: '#F9F9F8',
              border: '1px solid #EFEFEE',
              borderRadius: '4px',
              padding: '16px',
            }}
          >
            {scopes.map((scope) => (
              <div
                key={scope}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  marginBottom: '5px',
                }}
              >
                <CheckOutlined style={{ color: '#52C41A', fontSize: '16px' }} />
                <Text size="lg">{scope}</Text>
              </div>
            ))}
          </div>
        </Space>

        <Button
          type="primary"
          htmlType="button"
          dataTestId="grant-access-button"
          block
          style={{ height: '48px', padding: '10px' }}
          href={authorizeUrl}
        >
          {translations.grantButton}
        </Button>

        <Text size="sm" style={{ color: '#8C8C8C', textAlign: 'center' }}>
          {translations.disclaimer}
        </Text>
      </Space>
    </div>
  );
}
